import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, connect } from 'react-redux';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import RestaurantRoundedIcon from '@mui/icons-material/RestaurantRounded';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import StarOutlineRoundedIcon from '@mui/icons-material/StarOutlineRounded';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import Loader from '@components/Loader';
import NoDataFound from '@components/NoDataFound';
import { getRestaurants, getGlobalConfig } from '@utilities/auth';
import RestaurantCard from './RestaurantCard';

import './Dashboard.scss';

type FilterKey = 'all' | 'offers' | 'topRated' | 'fastest';

const FILTERS: { key: FilterKey; label: string; icon: React.ReactNode }[] = [
    { key: 'all', label: 'All', icon: <RestaurantRoundedIcon fontSize="small" /> },
    { key: 'offers', label: 'Offers', icon: <LocalOfferOutlinedIcon fontSize="small" /> },
    { key: 'topRated', label: 'Top rated', icon: <StarOutlineRoundedIcon fontSize="small" /> },
    { key: 'fastest', label: 'Fastest', icon: <BoltRoundedIcon fontSize="small" /> },
];

const isVisibleRestaurant = (restaurant: any) =>
    process.env.REACT_APP_FIRE_ENV !== 'prod' || restaurant?.isProd;

const getRatingValue = (restaurant: any) => Number(restaurant?.ratings || 0);

const getDeliveryMinutes = (restaurant: any) => {
    if (typeof restaurant?.deliveryMinutes === 'number') return restaurant.deliveryMinutes;
    if (typeof restaurant?.deliveryTime === 'string') {
        const match = restaurant.deliveryTime.match(/\d+/);
        return match ? Number(match[0]) : 30;
    }
    return 30;
};

const Dashboard = ({ restaurants, publicRestaurants, user, type, roles, selectedResId }: any) => {
    const dispatch = useDispatch();
    const _params = useParams();
    const navigate = useNavigate();
    const [searchQuery, setSearchQuery] = useState('');
    const [activeFilter, setActiveFilter] = useState<FilterKey>('all');

    const isAdminView = type === 'admin';
    const sourceList = isAdminView ? restaurants : publicRestaurants;

    useEffect(() => {
        if (user?.phoneNumber && roles && Object.keys(roles)?.length && selectedResId) {
            navigate(`/admin/${selectedResId}/orders`);
        } else {
            navigate(`/`);
        }
        if (
            !localStorage.getItem('SELECTED_RESTAURANT') ||
            !localStorage.getItem('RESTAURANTS_DATA') ||
            !localStorage.getItem('REACT_APP_VERSION') ||
            localStorage.getItem('REACT_APP_VERSION') !== process.env.REACT_APP_VERSION
        ) {
            getRestaurants(dispatch, roles);
        }
        if (
            user?.phoneNumber &&
            roles &&
            (!localStorage.getItem('GLOBAL_CONFIG') ||
                !localStorage.getItem('REACT_APP_VERSION') ||
                localStorage.getItem('REACT_APP_VERSION') !== process.env.REACT_APP_VERSION)
        ) {
            getGlobalConfig(dispatch);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run on selectedResId only to avoid re-fetch loops
    }, [selectedResId]);

    const visibleRestaurants = useMemo(
        () => (sourceList || []).filter(isVisibleRestaurant),
        [sourceList]
    );

    const filteredRestaurants = useMemo(() => {
        let list = [...visibleRestaurants];

        if (searchQuery.trim()) {
            const query = searchQuery.trim().toLowerCase();
            list = list.filter((restaurant: any) => {
                const name = restaurant?.name?.toLowerCase() || '';
                const cuisine = (restaurant?.foodType || restaurant?.type || '').toLowerCase();
                const address = restaurant?.address?.toLowerCase() || '';
                return name.includes(query) || cuisine.includes(query) || address.includes(query);
            });
        }

        if (activeFilter === 'offers') {
            list = list.filter(
                (restaurant: any) => restaurant?.discountPercent || restaurant?.extraDiscountPercent
            );
        }

        if (activeFilter === 'topRated') {
            list.sort((a: any, b: any) => getRatingValue(b) - getRatingValue(a));
        }

        if (activeFilter === 'fastest') {
            list.sort((a: any, b: any) => getDeliveryMinutes(a) - getDeliveryMinutes(b));
        }

        return list;
    }, [visibleRestaurants, searchQuery, activeFilter]);

    const offerCount = useMemo(
        () =>
            visibleRestaurants.filter(
                (restaurant: any) => restaurant?.discountPercent || restaurant?.extraDiscountPercent
            ).length,
        [visibleRestaurants]
    );

    const handleRestaurantClick = (restaurant: any) => {
        if (isAdminView) {
            navigate(`/admin/${restaurant.docId}/orders`);
            return;
        }
        navigate(`/restaurant/${restaurant.docId}`);
    };

    const isLoading = !sourceList?.length;
    const pageTitle = isAdminView ? 'Your restaurants' : 'Restaurants near you';

    return (
        <main className="dashboard">
            <section className="dashboard__hero">
                <div className="dashboard__hero-content">
                    <div className="dashboard__hero-row">
                        <h1 className="dashboard__title">{pageTitle}</h1>
                        <ul className="dashboard__chips" aria-label="Restaurant summary">
                            <li className="dashboard__chip">
                                <strong>{visibleRestaurants.length}</strong>
                                {isAdminView ? ' locations' : ' nearby'}
                            </li>
                            <li className="dashboard__chip">
                                <strong>{offerCount}</strong> offers
                            </li>
                            <li className="dashboard__chip">~30 min</li>
                        </ul>
                    </div>

                    <div className="dashboard__search">
                        <SearchRoundedIcon className="dashboard__search-icon" />
                        <input
                            type="search"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Search by name, cuisine, or area"
                            aria-label="Search restaurants"
                        />
                    </div>
                </div>
            </section>

            <section className="dashboard__content">
                <div className="dashboard__filters" role="tablist" aria-label="Restaurant filters">
                    {FILTERS.map(({ key, label, icon }) => (
                        <button
                            key={key}
                            type="button"
                            role="tab"
                            aria-selected={activeFilter === key}
                            className={`dashboard__filter ${activeFilter === key ? 'dashboard__filter--active' : ''}`}
                            onClick={() => setActiveFilter(key)}
                        >
                            {icon}
                            {label}
                        </button>
                    ))}
                </div>

                {isLoading ? (
                    <div className="dashboard__loader">
                        <Loader />
                    </div>
                ) : filteredRestaurants.length ? (
                    <div className="dashboard__grid">
                        {filteredRestaurants.map((restaurant: any) => (
                            <RestaurantCard
                                key={restaurant.docId}
                                data={restaurant}
                                onClick={() => handleRestaurantClick(restaurant)}
                            />
                        ))}
                    </div>
                ) : (
                    <NoDataFound
                        title={
                            searchQuery.trim()
                                ? 'No restaurants match your search'
                                : 'No restaurants available right now'
                        }
                        height="40vh"
                    />
                )}
            </section>
        </main>
    );
};

const mapStateToProps = (state: any) => {
    const { common, cart, user } = state;
    return {
        restaurants: common?.restaurants,
        publicRestaurants: common?.publicRestaurants,
        cart: (cart && cart[common?.selectedRestaurant?.docId]) || {},
        roles: user?.user?.roles || {},
        user: user?.user || null,
        selectedResId: common?.selectedRestaurant?.docId || null,
    };
};

export default connect(mapStateToProps)(Dashboard);
